import { Package, Users01, BookOpen01, File05, User01 } from "@untitledui/icons";
import type { ListBoxItemProps as AriaListBoxItemProps } from "react-aria-components";
import { ListBoxItem as AriaListBoxItem, Text as AriaText } from "react-aria-components";
import { cx } from "@/utils/cx";
import type { SearchResult } from "./command-menu-search";
import type { EntityType } from "./command-menu-context";

interface CommandMenuItemProps extends Omit<AriaListBoxItemProps<SearchResult>, "id" | "textValue"> {
    result: SearchResult;
    inputRef?: React.RefObject<HTMLInputElement | null>;
    listBoxRef?: React.RefObject<HTMLDivElement | null>;
}

const entityIcons: Record<NonNullable<EntityType>, typeof Package> = {
    container: Package,
    shipper: Users01,
    receiver: Users01,
    account: User01,
    address: BookOpen01,
    document: File05,
};

export const CommandMenuItem = ({ result, inputRef, listBoxRef, ...props }: CommandMenuItemProps) => {
    const Icon = (result.type && entityIcons[result.type]) || File05;
    const textValue = result.subtitle ? `${result.title} ${result.subtitle}` : result.title;

    return (
        <AriaListBoxItem
            id={result.id}
            textValue={textValue}
            {...props}
            className={(state) =>
                cx(
                    "w-full px-1.5 py-px outline-hidden",
                    typeof props.className === "function" ? props.className(state) : props.className
                )
            }
        >
            {(state) => (
                <div className="group block cursor-pointer px-2 py-0.5 outline-hidden">
                    <div
                        className={cx(
                            "relative flex min-h-10 items-center justify-between rounded-lg outline-focus-ring transition duration-100 ease-linear py-2 px-2.5",
                            state.isSelected && "bg-active",
                            state.isDisabled && "cursor-not-allowed opacity-50",
                            state.isFocused && !state.isSelected && "hover:bg-primary_hover bg-primary_hover",
                            state.isFocusVisible && "ring-2 ring-focus-ring ring-inset",
                        )}
                    >
                        <Icon className="mr-2 size-5 text-fg-quaternary shrink-0" aria-hidden="true" />
                        <div className="flex flex-1 gap-x-2">
                            <AriaText
                                slot="label"
                                className={cx("text-primary text-sm font-medium", state.isDisabled && "text-disabled")}
                            >
                                {result.title}
                            </AriaText>
                            {result.subtitle && (
                                <AriaText slot="description" className={cx("text-xs text-tertiary", state.isDisabled && "text-disabled")}>
                                    {result.subtitle}
                                </AriaText>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AriaListBoxItem>
    );
};
